import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, AlertCircle } from 'lucide-react';
import { gradientButton, gradientText } from '../theme';
import { useAppSettings } from '../hooks/useAppSettings';
import { getTransactionById, markTransactionAsUsed } from '../lib/transaction-helpers';

// Types de services disponibles
export type PaymentService = 
  | 'SHORT_TERM_RESULTS_UNLOCK'    // Pour SingleSearch.tsx
  | 'RELATION_DELETION'            // Pour Profile.tsx
  | 'SEARCH_DETAILS_UNLOCK';       // Pour Home.tsx

interface PaymentFlowProps {
  amount: number;
  currency: string;
  onSuccess: () => void;
  onCancel: () => void;
  title?: string;
  userName?: string;
  service: PaymentService;  // Nouveau prop pour identifier le service
}

interface PaymentInfo {
  number: string;
  reference: string;
}

const PaymentFlow: React.FC<PaymentFlowProps> = ({
  amount,
  currency,
  onSuccess,
  onCancel,
  title,
  userName = 'Utilisateur',
  service
}) => {
  const { t } = useTranslation();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState(1);
  const [copied, setCopied] = useState<string | null>(null);
  const [transactionId, setTransactionId] = useState('');
  const [transactionError, setTransactionError] = useState<string | null>(null);
  const [transactionSuccess, setTransactionSuccess] = useState(false);
  const { getMobilePaymentNumbers } = useAppSettings();
  
  // Vérifier les valeurs passées en props au montage du composant
  useEffect(() => {
    console.log("[DIAGNOSTIC] PaymentFlow monté avec les valeurs:", {
      service,
      amount,
      currency
    });
    
    return () => {
      console.log("[DIAGNOSTIC] PaymentFlow démonté");
    };
  }, [service, amount, currency]);

  // Ne pas afficher le composant si le service actuel est gratuit
  const isCurrentServiceFree = (): boolean => {
    // Au lieu d'interroger à nouveau les paramètres,
    // utiliser directement les valeurs reçues en props
    // Si amount > 0, alors le service n'est PAS gratuit
    console.log("[DIAGNOSTIC] Valeurs passées en props :", { amount, currency, service });
    return amount === 0;
  };

  // Si le service est gratuit, on n'affiche pas le composant mais on exécute le callback de succès
  if (isCurrentServiceFree()) {
    // Appeler onSuccess de façon asynchrone pour éviter des problèmes de rendu
    setTimeout(() => {
      onSuccess();
    }, 0);
    return null;
  }

  // Fonction pour obtenir le titre en fonction du service
  const getServiceTitle = () => {
    switch (service) {
      case 'SHORT_TERM_RESULTS_UNLOCK':
        return t('payment.services.shortTermUnlock', 'Déverrouiller les rencontres court terme');
      case 'RELATION_DELETION':
        return t('profile.deleteRelation.title', 'Mettre fin à la relation');
      case 'SEARCH_DETAILS_UNLOCK':
        return t('payment.services.searchDetailsUnlock', 'Déverrouiller les détails de recherche');
      default:
        return title || t('payment.title', 'Paiement');
    }
  };

  const handlePaymentMethodSelect = (method: string) => {
    setSelectedPaymentMethod(method);
  };
  
  const handleProceed = async () => {
    if (step === 1 && selectedPaymentMethod) {
      setStep(2);
      return;
    }

    if (step === 2) {
      if (!transactionId.trim()) {
        setTransactionError(t('payment.errors.noTransactionId', 'Veuillez entrer un ID de transaction'));
        return;
      }

      setProcessing(true);
      setTransactionError(null);

      try {
        // Vérifier si la transaction existe
        const transaction = await getTransactionById(transactionId);
        
        if (!transaction) {
          setTransactionError(t('payment.errors.invalidTransaction', 'ID de transaction invalide ou introuvable'));
          setProcessing(false);
          return;
        }

        // Vérifier si le montant correspond exactement
        if (transaction.montant !== amount) {
          setTransactionError(
            t('payment.errors.amountMismatch', 
              `Le montant de la transaction (${transaction.montant} ${currency}) ne correspond pas au montant requis (${amount} ${currency})`
            )
          );
          setProcessing(false);
          return;
        }

        // Marquer la transaction comme utilisée avec jusqu'à 3 tentatives
        let markSuccess = false;
        for (let attempt = 1; attempt <= 3 && !markSuccess; attempt++) {
          console.log(`[Payment] Tentative ${attempt} de marquer la transaction ${transactionId} comme utilisée`);
          markSuccess = await markTransactionAsUsed(transactionId);
          
          if (!markSuccess && attempt < 3) {
            // Attendre un court délai avant la prochaine tentative
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }

        if (!markSuccess) {
          console.warn(`[Payment] Échec du marquage de la transaction ${transactionId} après 3 tentatives, mais le service sera débloqué`);
        } else {
          console.log(`[Payment] Transaction ${transactionId} marquée comme utilisée avec succès`);
        }

        // Transaction validée avec succès
        setTransactionSuccess(true);
        
        // Attendre un peu pour montrer le succès avant de fermer
        setTimeout(() => {
          onSuccess();  // Appeler la fonction de succès du parent
        }, 1500);

      } catch (error) {
        console.error('Erreur lors de la vérification de la transaction:', error);
        setTransactionError(t('payment.errors.verificationError', 'Erreur lors de la vérification de la transaction'));
        setProcessing(false);
      }
    }
  };

  const handleCopy = (text: string, type: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopied(type);
        setTimeout(() => setCopied(null), 2000);
      })
      .catch(err => console.error('Erreur lors de la copie:', err));
  };
  
  const mobilePaymentNumbers = getMobilePaymentNumbers();
  const orangeMoneyNumbers = mobilePaymentNumbers.orange_money;
  const mtnMoneyNumbers = mobilePaymentNumbers.mtn_money;
  
  // Pour masquer ou afficher Orange Money facilement
  const showOrangeMoney = false; // Remettre à true pour réactiver l'affichage
  
  const renderPaymentDetails = () => {
    if (!selectedPaymentMethod) return null;
    
    const paymentNumbers = selectedPaymentMethod === 'orange' 
      ? orangeMoneyNumbers 
      : mtnMoneyNumbers;
    
    return (
      <div className="payment-details">
        {paymentNumbers.map((paymentInfo: PaymentInfo, index: number) => (
          <div key={index} className="payment-number-container mb-3">
            <div className="payment-number flex justify-between items-center">
              <span>{paymentInfo.number}</span>
              <div className="flex flex-col items-center">
                <button
                  className="copy-button flex items-center space-x-1 bg-yellow-500 hover:bg-yellow-600 px-2 py-1 rounded-md transition-colors"
                  onClick={() => handleCopy(paymentInfo.number, `${selectedPaymentMethod}-${index}`)}
                >
                  {copied === `${selectedPaymentMethod}-${index}` ? (
                    <Check className="w-3 h-3 text-black" />
                  ) : (
                    <Copy className="w-3 h-3 text-black" />
                  )}
                </button>
                {copied === `${selectedPaymentMethod}-${index}` && (
                  <span className="text-xs text-gray-400 mt-1">{t('payment.copied', 'Copié')}</span>
                )}
              </div>
            </div>
            <p className="payment-reference text-sm text-gray-400 mt-1">
              {paymentInfo.reference.replace('$userName', userName)}
            </p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-50 p-4">
      <div className="w-full h-full max-w-xl mx-auto flex flex-col justify-center">
        {/* En-tête avec icône et titre */}
        <div className="mb-6 text-center">
          <h2 className={`text-2xl font-bold ${gradientText} mb-3`}>
            {getServiceTitle()}
          </h2>
          {/* Émojis animés uniquement pour les résultats court terme */}
          {service === 'SHORT_TERM_RESULTS_UNLOCK' && (
            <div className="flex justify-center space-x-2 mb-2">
              <span className="animate-float text-2xl" style={{animationDelay: '0s'}}>🍆</span>
              <span className="animate-float text-2xl" style={{animationDelay: '0.3s'}}>🍑</span>
              <span className="animate-float text-2xl" style={{animationDelay: '0.6s'}}>🥂</span>
              <span className="animate-float text-2xl" style={{animationDelay: '0.9s'}}>💵</span>
            </div>
          )}
        </div>
        
        {step === 1 ? (
          <div className="space-y-4">
            <div className="bg-[#0A0A0A] rounded-xl p-4 border border-gray-800">
              <h3 className="text-base font-semibold text-white mb-4">
                {t('payment.stepTwo', 'Sélection du mode de paiement')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Orange Money - Masqué si showOrangeMoney est false */}
                {showOrangeMoney && (
                  <div
                    className={`flex items-center p-3 rounded-lg cursor-pointer transition-all duration-300 ${
                      selectedPaymentMethod === 'orange' 
                        ? 'bg-primary-end/10 border border-primary-end shadow-lg shadow-primary-end/20' 
                        : 'border border-gray-800 hover:bg-gray-800/50'
                    }`}
                    onClick={() => handlePaymentMethodSelect('orange')}
                  >
                    <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center mr-3">
                      <span className="text-white text-sm font-bold">OM</span>
                    </div>
                    <span className="text-white text-sm">{t('payment.orangeMoney', 'Orange Money')}</span>
                    {selectedPaymentMethod === 'orange' && (
                      <Check className="w-4 h-4 text-primary-end ml-auto" />
                    )}
                  </div>
                )}
                {/* MTN Mobile Money - Toujours affiché */}
                <div
                  className={`flex items-center p-3 rounded-lg cursor-pointer transition-all duration-300 ${
                    selectedPaymentMethod === 'mtn' 
                      ? 'bg-primary-end/10 border border-primary-end shadow-lg shadow-primary-end/20' 
                      : 'border border-gray-800 hover:bg-gray-800/50'
                  }`}
                  onClick={() => handlePaymentMethodSelect('mtn')}
                >
                  <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center mr-3">
                    <span className="text-black text-sm font-bold">MTN</span>
                  </div>
                  <span className="text-white text-sm">{t('payment.mtnMoney', 'MTN Mobile Money')}</span>
                  {selectedPaymentMethod === 'mtn' && (
                    <Check className="w-4 h-4 text-primary-end ml-auto" />
                  )}
                </div>
              </div>
            </div>
            
            {selectedPaymentMethod && (
              <div className="bg-gradient-to-r from-primary-start to-primary-end p-0.5 rounded-xl">
                <div className="bg-[#0A0A0A] p-4 rounded-xl">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-400 text-sm">{t('payment.amount', 'Montant')}:</span>
                    <span className="text-xl font-bold text-yellow-500">{amount} {currency}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm">{t('payment.paymentMethods', 'Mode de paiement')}:</span>
                    <span className="text-base text-white">
                      {selectedPaymentMethod === 'orange' 
                        ? t('payment.orangeMoney', 'Orange Money') 
                        : t('payment.mtnMoney', 'MTN Mobile Money')}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-[#0A0A0A] rounded-xl p-4 border border-gray-800">
            <h3 className="text-base font-semibold text-white mb-4">
              {t('payment.manualTransfer', 'Informations pour le transfert manuel')}
            </h3>
            
            <div className="bg-gradient-to-r from-primary-start to-primary-end p-0.5 rounded-lg mb-4">
              <div className="bg-[#17171d] p-4 rounded-lg">
                <div className="text-white text-sm mb-3">
                  {t('payment.sendAmount', 'Envoyez')} <span className="text-yellow-500 font-medium">{amount} {currency}</span> {t('payment.to', 'à')} :
                </div>
                
                {renderPaymentDetails()}
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="text-gray-400 text-xs">
                <span dangerouslySetInnerHTML={{ 
                  __html: t('payment.manualTransferNote', 'Après avoir effectué le transfert, collez l\'ID de transaction reçu ci-dessous.')
                }} />
              </div>
              
              <div>
                <label htmlFor="transactionId" className="block text-gray-400 text-xs mb-1">
                  {t('payment.transactionId', 'ID de transaction')}:
                </label>
                <input
                  id="transactionId"
                  type="text"
                  value={transactionId}
                  onChange={(e) => {
                    setTransactionId(e.target.value);
                    setTransactionError(null);
                  }}
                  placeholder={t('payment.transactionIdPlaceholder', 'Ex: TRANS_2024_001')}
                  className={`w-full bg-[#0A0A0A] border ${
                    transactionError ? 'border-red-500' : transactionSuccess ? 'border-green-500' : 'border-gray-800'
                  } rounded-lg p-3 text-white text-sm focus:outline-none focus:border-primary-end transition-colors`}
                  disabled={processing || transactionSuccess}
                />
                {transactionError && (
                  <div className="mt-1 text-red-500 text-xs flex items-center">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    {transactionError}
                  </div>
                )}
                {transactionSuccess && (
                  <div className="mt-1 text-green-500 text-xs flex items-center">
                    <Check className="w-3 h-3 mr-1" />
                    {t('payment.success', 'Transaction validée avec succès !')}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        <div className="flex gap-3 mt-6">
          <button
            onClick={step === 1 ? onCancel : () => setStep(1)}
            className="flex-1 py-3 px-4 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors"
            disabled={processing || transactionSuccess}
          >
            {step === 1 ? t('common.cancel', 'Annuler') : t('common.back', 'Retour')}
          </button>
          
          <button
            onClick={handleProceed}
            disabled={
              (step === 1 && !selectedPaymentMethod) || 
              processing || 
              transactionSuccess
            }
            className={`flex-1 ${gradientButton} text-white py-3 px-4 rounded-lg text-sm font-medium ${
              ((step === 1 && !selectedPaymentMethod) || processing || transactionSuccess) 
                ? 'opacity-50 cursor-not-allowed' 
                : ''
            }`}
          >
            {processing ? (
              <div className="flex items-center justify-center">
                <div className="w-4 h-4 mr-2 border-2 border-t-transparent border-white rounded-full animate-spin"></div>
                {t('payment.processing', 'Vérification...')}
              </div>
            ) : transactionSuccess ? (
              <div className="flex items-center justify-center">
                <Check className="w-4 h-4 mr-2" />
                {t('payment.validated', 'Validé')}
              </div>
            ) : (
              step === 1 ? 
                t('payment.continue', 'Continuer') : 
                t('payment.verify', 'Vérifier la transaction')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentFlow; 